/**
 * 安全周报下载配置
 */
import { DownloadConfigInfoType, CommonColumnsType } from '../types';
import { getDetailTable } from '@op/services';
import {
  languageRenderHelper,
  channelRenderHelper,
  zeroShowDefaultValue,
  ratioReduce,
} from '../renderHelper';
import {
  DIMENSION_OPTIONS,
  QUADRANT_CONFIG,
} from '@op/pages/SecurityWeeklyReport/constants';
import { channelInfo } from '@op/components/ChannelLabel';
import { dealWithOthersName } from '@op/utils/languageValue';
import { formatDateFunc } from '@op/utils/time';

// 四象限处理后的数据类型
interface IProcessedQuadrantItem {
  category: string;
  sub_category: string;
  mentions: number;
  avg_engagement_intensity: number;
  change_rate: number;
  quadrant: 1 | 2 | 3 | 4;
}

// ========== 关键指标下载配置 ==========

/**
 * 关键指标趋势下载配置
 * 数据格式: { date, mentions, mentions_ratio, keyword_updated? }
 */
export const WeeklyKeyMetrics: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: 'Sheet1',

  columns: (params): CommonColumnsType => {
    const { auxiliaryParams } = params;
    const filter = auxiliaryParams?.filter || {};

    return [
      {
        key: 'date',
        header: 'Date',
        renderHelper: [
          (item: { [key: string]: any }, key: string) => {
            return formatDateFunc(filter.date_type)(item[key]);
          },
        ],
      },
      {
        key: 'mentions',
        header: 'Mentions',
        numFmt: '0',
      },
      {
        key: 'mentions_ratio',
        header: 'Mentions Ratio',
        numFmt: '0.00%',
      },
    ];
  },
};

// ========== 明细表下载配置 ==========

export const WeeklyReportDetailTable: DownloadConfigInfoType = {
  type: 'page',
  sheetNames: 'Detail Table',
  valueKey: 'table_data',
  totalKey: 'total',
  // 动态列配置，根据选中的维度生成
  columns: ({ auxiliaryParams }: any) => {
    const { dimensions = [] } = auxiliaryParams || {};

    const columns: any[] = [];

    // 基础列 - 主维度（始终显示）
    columns.push({
      key: 'category',
      header: 'Dimension',
      width: 25,
    });

    // 根据选中的维度添加动态列
    DIMENSION_OPTIONS.forEach(option => {
      if (!dimensions.includes(option.value)) return;

      const column: any = {
        key: option.columnKey,
        header: option.columnLabel,
        width: option.value === 'date' ? 15 : 20,
      };

      // 特殊处理语言和渠道
      if (option.value === 'language') {
        column.renderHelper = [languageRenderHelper];
      } else if (option.value === 'channel') {
        column.renderHelper = [channelRenderHelper];
      }

      // date 列放在最前面
      if (option.value === 'date') {
        columns.unshift(column);
      } else {
        columns.push(column);
      }
    });

    // 数值列（始终显示）
    columns.push(
      {
        key: 'current_volume',
        header: 'Current Volume',
      },
      {
        key: 'previous_volume',
        header: 'Previous Volume',
      },
      {
        key: 'change_rate',
        header: 'Change Rate',
        renderHelper: [ratioReduce(100)],
        numFmt: ['0.00%'],
      },
      {
        key: 'contribution_rate',
        header: 'Contribution Rate',
        renderHelper: [ratioReduce(100)],
        numFmt: ['0.00%'],
      },
    );

    return columns;
  },

  downloadFunc: async filter => {
    const res = await getDetailTable({ data: filter });
    return {
      table_data: res.table_data || [],
      total: res.total,
    };
  },
};

// ========== 维度趋势下载配置 ==========

/**
 * 语言维度趋势下载配置
 */
export const WeeklyLanguage: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: 'Sheet1',

  columns: (params): CommonColumnsType => {
    const { data, auxiliaryParams } = params;
    const filter = auxiliaryParams?.filter || {};

    // 获取所有维度列（排除 date、total 和内部字段）
    // 遍历所有行收集所有可能的 key，因为不同日期可能有不同的语言
    if (!data?.length) return [];
    const dimensionKeySet = new Set<string>();
    data.forEach((row: Record<string, any>) => {
      Object.keys(row).forEach(key => {
        if (key !== 'date' && key !== 'total' && !key.startsWith('_')) {
          dimensionKeySet.add(key);
        }
      });
    });
    const dimensionKeys = Array.from(dimensionKeySet);

    const columns: CommonColumnsType = [
      {
        key: 'date',
        header: 'Date',
        renderHelper: [
          (item: { [key: string]: any }, key: string) => {
            return formatDateFunc(filter.date_type)(item[key]);
          },
        ],
      },
    ];

    // 动态添加各语言列
    dimensionKeys.forEach(key => {
      columns.push({
        key,
        header: dealWithOthersName(key, 'language', 'en-US'),
        numFmt: '0',
      });
    });

    return columns;
  },
};

/**
 * 渠道维度趋势下载配置
 */
export const WeeklyChannel: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: 'Sheet1',

  columns: (params): CommonColumnsType => {
    const { data, auxiliaryParams } = params;
    const filter = auxiliaryParams?.filter || {};

    // 获取所有维度列（排除 date、total 和内部字段）
    // 遍历所有行收集所有可能的 key，因为不同日期可能有不同的渠道
    if (!data?.length) return [];
    const dimensionKeySet = new Set<string>();
    data.forEach((row: Record<string, any>) => {
      Object.keys(row).forEach(key => {
        if (key !== 'date' && key !== 'total' && !key.startsWith('_')) {
          dimensionKeySet.add(key);
        }
      });
    });
    const dimensionKeys = Array.from(dimensionKeySet);

    const columns: CommonColumnsType = [
      {
        key: 'date',
        header: 'Date',
        renderHelper: [
          (item: { [key: string]: any }, key: string) => {
            return formatDateFunc(filter.date_type)(item[key]);
          },
        ],
      },
    ];

    // 动态添加各渠道列
    dimensionKeys.forEach(key => {
      const { name } = channelInfo(key.toLowerCase() as any);
      columns.push({
        key,
        header: name || key,
        numFmt: '0',
      });
    });

    return columns;
  },
};

/**
 * 举报类型趋势下载配置
 */
export const WeeklyReportType: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: 'Sheet1',
  columns: (params): CommonColumnsType => {
    const { data, auxiliaryParams } = params;
    const { filter = {}, columns: list = [] } = auxiliaryParams || {};

    // 获取所有维度列（排除 date、total 和内部字段）
    if (!data?.length) return [];

    const columns: CommonColumnsType = [
      {
        key: 'date',
        header: 'Date',
        renderHelper: [
          (item: { [key: string]: any }, key: string) => {
            return formatDateFunc(filter.date_type)(item[key]);
          },
        ],
      },
    ];

    // 动态添加各举报类型列
    list.forEach((key: string) => {
      columns.push({
        key,
        header: key,
        numFmt: '0',
        renderHelper: [zeroShowDefaultValue],
      });
    });

    return columns;
  },
};

/**
 * 子话题趋势下载配置
 */
export const WeeklySubTopic: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: 'Sheet1',

  columns: (params): CommonColumnsType => {
    const { data, auxiliaryParams } = params;
    const { filter, columns: keys = [] } = auxiliaryParams || {};

    // 获取所有维度列（排除 date、total 和内部字段）
    // 遍历所有行收集所有可能的 key，因为不同日期可能有不同的子话题
    if (!data?.length) return [];
    const columns: CommonColumnsType = [
      {
        key: 'date',
        header: 'Date',
        renderHelper: [
          (item: { [key: string]: any }, key: string) => {
            return formatDateFunc(filter.date_type)(item[key]);
          },
        ],
      },
    ];

    // 动态添加各子话题列
    keys.forEach((key: any) => {
      columns.push({
        key,
        header: key,
        numFmt: '0',
        renderHelper: [],
      });
    });

    return columns;
  },
};

/**
 * 关键词变化下载配置 (平铺格式：日期，关键词(三语)，声量)
 */
export const WeeklyKeywords: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: 'Sheet1',
  columns: (params): CommonColumnsType => {
    const { auxiliaryParams } = params;
    const isEn = auxiliaryParams?.isEn;
    return [
      {
        key: 'date',
        header: isEn ? 'Date' : '日期',
        width: 15,
      },
      {
        key: 'keyword',
        header: isEn ? 'Keyword (Origin)' : '关键词 (源语言)',
        width: 25,
      },
      {
        key: 'keyword_cn',
        header: isEn ? 'Keyword (CN)' : '关键词 (中文)',
        width: 25,
      },
      {
        key: 'keyword_en',
        header: isEn ? 'Keyword (EN)' : '关键词 (英文)',
        width: 25,
      },
      {
        key: 'volume',
        header: isEn ? 'Mentions' : '声量',
        numFmt: '0',
        width: 15,
      },
    ];
  },
  dealWithDataFunc: ({ data, auxiliaryParams }) => {
    const { filter = {}, keywordMap = {} } = auxiliaryParams || {};

    return data.map((row: any) => {
      // 获取关键词多语言信息
      const item = keywordMap[row.key];

      return {
        date: formatDateFunc(filter.date_type)(row.time),
        keyword: item?.keyword || row.key,
        keyword_cn: item?.keyword_cn || '-',
        keyword_en: item?.keyword_en || '-',
        volume: row.value,
      };
    });
  },
};

// ========== 四象限散点图下载配置 ==========

/**
 * 四象限散点图下载配置
 * 子话题发酵分析
 * 表头: 日期, 举报类型, 子话题, 声量, 平均互动强度, 变化率, 风险类型
 */
export const WeeklyQuadrant: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: 'Sheet1',

  columns: (): CommonColumnsType => [
    {
      key: 'date',
      header: 'Date',
      width: 15,
    },
    {
      key: 'category',
      header: 'Category',
      width: 20,
    },
    {
      key: 'sub_category',
      header: 'Subtopic',
      width: 30,
    },
    {
      key: 'mentions',
      header: 'Mentions',
      numFmt: '0',
      width: 15,
    },
    {
      key: 'avg_engagement_intensity',
      header: 'Avg Engagement Intensity',
      numFmt: '0.00',
      width: 20,
    },
    {
      key: 'change_rate',
      header: 'Change Rate',
      numFmt: '0.00%',
      width: 15,
    },
    {
      key: 'risk_type',
      header: 'Risk Type',
      width: 35,
    },
  ],

  dealWithDataFunc: ({ data, auxiliaryParams }) => {
    const filter = auxiliaryParams?.filter || {};
    return data.map((item: IProcessedQuadrantItem) => ({
      date: filter?.end_time || '',
      category: item.category,
      sub_category: item.sub_category,
      mentions: item.mentions,
      avg_engagement_intensity: item.avg_engagement_intensity ?? '-',
      change_rate: item.change_rate ?? '-',
      risk_type: QUADRANT_CONFIG[item.quadrant]?.label || '',
    }));
  },
};
